// Copyright (c) Ping. All rights reserved.
//
// Conversation-list / thread-header message-preview row. The preview formatter
// (`appEvent/formatPreview.ts`) encodes the message kind as a leading emoji
// glyph — "📷 Photo", "🎤 Voice message · 0:12", etc. Here we lift that glyph
// out and render it as a lucide icon (the same `splitPreviewGlyph` mapping the
// reply-quote / pinned-bar / composer previews use), so NO chat surface ever
// shows the raw emoji. The stored `lastMessagePreview` string is untouched.
//
// Colour follows the row tone: muted by default, brighter when the row has
// unread messages (mirrors the text).

import { Text, View } from 'react-native';
import { splitPreviewGlyph } from '../appEvent/PreviewLabel';
import { useColorScheme } from '../foundations/colorScheme';
import { tokens } from '../design-system/tokens';

export interface MessagePreviewProps {
  /** Formatted preview string straight off the conversation row. */
  readonly preview: string;
  /** Brightens the text + icon to the primary tone (unread rows). */
  readonly unread?: boolean;
  /** Extra classes for the text node (the row's tone classes live here). */
  readonly className?: string;
}

export function MessagePreview({
  preview,
  unread = false,
  className = '',
}: MessagePreviewProps): JSX.Element {
  const scheme = useColorScheme() === 'dark' ? 'dark' : 'light';
  const { Icon, label } = splitPreviewGlyph(preview);

  const palette = tokens.color.semantic[scheme];
  const iconColor = unread ? palette.text.primary : palette.text.secondary;

  return (
    // Icon + text — horizontal, centre-aligned, 4px gap (Figma itemSpacing).
    <View className="flex flex-row items-center gap-1 flex-1 min-w-0">
      {Icon !== null && (
        <View className="shrink-0">
          <Icon size={12} strokeWidth={2} color={iconColor} />
        </View>
      )}
      <Text
        numberOfLines={1}
        className={`font-sans font-normal text-[14px] leading-[20px] flex-1 min-w-0 ${className}`}
      >
        {label}
      </Text>
    </View>
  );
}
